<template>
  <md-card class="recordings-page">
    <md-card-header class="md-card-header-icon md-card-header-primary">
      <div class="card-icon">
        <md-icon>videocam</md-icon>
      </div>
      <md-toolbar class="fm-card-header">
        <h4 class="md-title">Search Filters</h4>
        <help-link :link="helpLink.link" title="searchFilter"></help-link>
      </md-toolbar>
      <div class="md-layout">
        <span class="md-layout-item md-caption">
          Enter recording information and click <b>Search </b>to search
          recordings. All times shown are in
          <strong>{{ getTimezoneString() }}</strong
          >.
        </span>
      </div>
    </md-card-header>
    <md-card-content>
      <div class="md-layout">
        <div class="md-layout-item md-size-50 md-small-size-100">
          <md-field
            :class="{ 'md-invalid': !validField.appId && filtersValidated }"
            md-clearable
            @md-clear="validateFilters"
            v-on:keyup="validateFilters"
            v-on:change="validateFilters"
          >
            <label>Application ID</label>
            <md-input
              v-model="unsearchedFilter.appId"
              v-on:keyup="validateFilters"
              v-on:change="validateFilters"
              type="text"
              list="applicationList"
              class="md-primary"
              id="appIdInput"
              autocomplete="off"
              required
            >
            </md-input>
            <span class="md-error">
              Application ID does not exist
            </span>
            <datalist id="applicationList">
              <option v-for="app in apps" :key="app.id">
                {{ app.applicationId }}
              </option>
            </datalist>
          </md-field>
        </div>
        <div class="md-layout-item md-size-50 md-small-size-100">
          <md-field md-clearable @md-clear="validateFilters">
            <label>Channel ID</label>
            <md-input v-model="unsearchedFilter.channelId"></md-input>
          </md-field>
        </div>
        <span class="md-layout-item md-size-100 md-body-2 timestampLabel">
          Timestamp Min
        </span>
        <div class="md-layout-item md-size-50 md-small-size-100">
          <md-field
            md-clearable
            @md-clear="validateFilters"
            :class="{ 'md-invalid': !validField.minDate && filtersValidated }"
          >
            <md-input
              type="date"
              v-on:keyup="validateFilters"
              v-on:change="validateFilters"
              v-model="unsearchedFilter.minDate"
              md-immediately
              md-override-native
              :class="{ 'md-invalid': !validField.minDate && filtersValidated }"
            >
            </md-input>
            <label>Date</label>
            <span class="md-error">
              Invalid Minimum Date
            </span>
          </md-field>
        </div>
        <div class="md-layout-item md-size-50 md-small-size-100">
          <md-field
            md-clearable
            @md-clear="validateFilters"
            :class="{ 'md-invalid': !validField.minTime && filtersValidated }"
          >
            <md-input
              v-on:keyup="validateFilters"
              v-on:change="validateFilters"
              v-model="unsearchedFilter.minTime"
              type="time"
              step="300"
            ></md-input>
            <label>Time (hh:mm AM/PM)</label>
            <span class="md-error">
              Invalid Minimum Time
            </span>
          </md-field>
        </div>
        <span class="md-layout-item md-size-100 md-body-2 timestampLabel">
          Timestamp Max
        </span>
        <div class="md-layout-item md-size-50 md-small-size-100">
          <md-field
            md-clearable
            @md-clear="validateFilters"
            :class="{ 'md-invalid': !validField.maxDate && filtersValidated }"
          >
            <md-input
              type="date"
              v-on:keyup="validateFilters"
              v-on:change="validateFilters"
              v-model="unsearchedFilter.maxDate"
              md-immediately
              md-override-native
              :class="{ 'md-invalid': !validField.maxDate && filtersValidated }"
            >
            </md-input>
            <label>Date</label>
            <span class="md-error">
              Invalid Maximum Date
            </span>
          </md-field>
        </div>
        <div class="md-layout-item md-size-50 md-small-size-100">
          <md-field
            md-clearable
            @md-clear="validateFilters"
            :class="{ 'md-invalid': !validField.maxTime && filtersValidated }"
          >
            <md-input
              v-on:keyup="validateFilters"
              v-on:change="validateFilters"
              v-model="unsearchedFilter.maxTime"
              type="time"
              step="300"
            ></md-input>
            <label>Time (hh:mm AM/PM)</label>
            <span class="md-error">
              Invalid Maximum Time
            </span>
          </md-field>
        </div>
        <div class="md-layout-item md-size-100" id="muxOnlyToggleContainer">
          <md-checkbox v-model="unsearchedFilter.muxOnly" class="md-primary">
            Muxed Only
          </md-checkbox>
        </div>
        <div class="md-layout-item md-size-15 md-small-size-100">
          <md-button
            v-on:click="search"
            :disabled="!searchReady"
            class="md-primary"
            type="submit"
            id="searchBtn"
            value="submit"
          >
            Search
          </md-button>
        </div>
      </div>
    </md-card-content>
  </md-card>
</template>

<script>
import { get } from "vuex-pathify";
import { HelpLinkTemplate } from "@/fmComponents";
import helpLinks from "../../contextualHelpLinks.js";
const moment = require("moment");

export default {
  components: {
    "help-link": HelpLinkTemplate
  },
  data: () => ({
    minDateInput: null,
    maxDateInput: null,
    validField: {
      appId: true,
      minDate: true,
      minTime: true,
      maxDate: true,
      maxTime: true
    },
    unsearchedFilter: {
      appId: null,
      channelId: null,
      minDate: null,
      minTime: null,
      maxDate: null,
      maxTime: null,
      muxOnly: false
    },
    helpLink: helpLinks.recordings,
    searchReady: false,
    filtersValidated: false
  }),
  computed: {
    apps: get("applications/entities")
  },
  created() {
    this.$material.locale.dateFormat = "yyyy-mm-dd";
    this.$store.dispatch("applications/loadEntities");
  },
  methods: {
    getTimezoneString() {
      //returns the current timezone, e.g. "Pacific Standard Time"
      return new Date().toString().match(/\((.*)\)/)[1];
    },
    validateDate(date, hideError) {
      if (!date) {
        return true;
      }
      let dateTime = moment(date);
      if (dateTime.isValid()) {
        hideError = true;
        return true;
      } else {
        hideError = false;
        return false;
      }
    },
    validateTime(time, hideError) {
      if (!time) {
        return true;
      }
      let dateString = moment().format("YYYY-MM-DD");
      let dateTime = moment(`${dateString} ${time}`, "YYYY-MM-DD HH:mm");
      if (dateTime.isValid()) {
        hideError = true;
        return true;
      } else {
        hideError = false;
        return false;
      }
    },
    //accesses the text input fields internal to vue-material's Datepicker
    //Prevents text input from the user so they must use the date picker to select a valid date.
    validateTimestamps() {
      return (
        this.validateDate(
          this.unsearchedFilter.minDate,
          this.validField.minDate
        ) &&
        this.validateTime(
          this.unsearchedFilter.minTime,
          this.validField.minTime
        ) &&
        this.validateDate(
          this.unsearchedFilter.maxDate,
          this.validField.maxDate
        ) &&
        this.validateTime(
          this.unsearchedFilter.maxTime,
          this.validField.maxTime
        )
      );
    },
    validateAppId() {
      this.filtersValidated = true;
      if (
        this.unsearchedFilter.appId == "" ||
        this.apps.some(app => app.applicationId === this.unsearchedFilter.appId)
      ) {
        this.validField.appId = true;
        return true;
      } else {
        this.validField.appId = false;
        return false;
      }
    },
    validateFilters() {
      if (this.validateAppId() && this.validateTimestamps()) {
        this.searchReady = true;
      } else {
        this.searchReady = false;
      }
    },
    updateEmptyDateOrTime() {
      //if searching with a time and no date, set date to today
      if (this.unsearchedFilter.minTime && !this.unsearchedFilter.minDate) {
        this.unsearchedFilter.minDate = moment().format("YYYY-MM-DD");
      }
      if (this.unsearchedFilter.maxTime && !this.unsearchedFilter.maxDate) {
        this.unsearchedFilter.maxDate = moment().format("YYYY-MM-DD");
      }
      //if searching with a date and no time, set time to start of day for minTimstamp and end of day for maxTimestamp
      if (!this.unsearchedFilter.maxTime && this.unsearchedFilter.maxDate) {
        this.unsearchedFilter.maxTime = moment()
          .endOf("day")
          .format("HH:mm");
      }
      if (!this.unsearchedFilter.minTime && this.unsearchedFilter.minDate) {
        this.unsearchedFilter.minTime = moment()
          .startOf("day")
          .format("HH:mm");
      }
    },
    search() {
      this.clean();
      this.updateEmptyDateOrTime();
      this.$emit("searchTriggered", this.unsearchedFilter);
    },
    clean() {
      if (this.unsearchedFilter.minDate == "") {
        this.unsearchedFilter.minDate = null;
      }
      if (this.unsearchedFilter.minTime == "") {
        this.unsearchedFilter.minTime = null;
      }
      if (this.unsearchedFilter.maxDate == "") {
        this.unsearchedFilter.maxDate = null;
      }
      if (this.unsearchedFilter.maxTime == "") {
        this.unsearchedFilter.maxTime = null;
      }
    }
  }
};
</script>

<style lang="scss">
// Couldn't get these to work /deep/ in scoped
@import "../../assets/scss/fm/colours";
@import "../../assets/scss/md/variables";

#appIdInput {
  color: $fm-navy;
  &::-webkit-calendar-picker-indicator {
    padding-bottom: 18px;
  }
}
.md-field label {
  top: -0.8rem;
  font-size: 14px;
}

.timestampLabel {
  margin-top: 10px;
}

.md-datepicker-dialog {
  .md-datepicker-header {
    background: $brand-primary-dark !important;
  }
  .md-datepicker-today:not(.md-datepicker-selected) {
    background: $fm-white !important;
    color: $fm-green !important;
    box-shadow: none;
  }
}

.md-field.md-clearable .md-input {
  padding-right: 40px;
}
input[type="time"] {
  &::-webkit-datetime-edit-hour-field,
  &::-webkit-datetime-edit-minute-field,
  &::-webkit-datetime-edit-ampm-field {
    &:focus {
      background-color: $brand-primary;
    }
  }
}
.recordings-page .md-layout-item {
  //Adds space below inputs for red error message
  padding-bottom: 10px;
  position: relative;
}
.recordings-page .md-error {
  color: $brand-danger;
  font-size: 12px;
  position: absolute;
  bottom: -12px;
  left: 5px;
}
#muxOnlyToggleContainer {
  .md-checked .md-checkbox-container {
    border-color: $fm-primary-dark-blue !important;
  }
  .md-ripple {
    color: $fm-green !important;
  }
}

#searchBtn {
  width: 100%;
}
</style>
